import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { analyticsDB } from "@/lib/db";
import { useRealtime } from "@/hooks/useRealtime";
import { Activity, AlertTriangle, BarChart3, Cpu, DollarSign, Zap, GitBranch, MessageSquare } from "lucide-react";
import { Skeleton } from "@/components/Skeleton";
import { notifyError } from "@/lib/errorToast";

type Stats = Awaited<ReturnType<typeof analyticsDB.last24h>>;

export function DevOps() {
  const { user } = useAuth();
  const [s, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const data = await analyticsDB.last24h();
      setStats(data);
    } catch (e) {
      notifyError(e as Error, "Falha ao carregar métricas");
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useRealtime("sandbox_runs", refresh);
  useRealtime("workflow_runs", refresh);
  useRealtime("chat_messages", refresh);

  const cards = [
    { label: "Sandbox 24h", value: s?.sandbox_runs ?? 0, sub: "tool runs", icon: Activity, color: "primary" },
    { label: "Workflows 24h", value: s?.workflow_runs ?? 0, sub: `${s?.workflow_errors ?? 0} failed`, icon: GitBranch, color: "secondary" },
    { label: "Chat msgs 24h", value: s?.chat_messages ?? 0, sub: "user + assistant", icon: MessageSquare, color: "accent" },
    { label: "Errors", value: s?.sandbox_errors ?? 0, sub: (s?.sandbox_errors ?? 0) === 0 ? "all green" : "atenção", icon: AlertTriangle, color: "destructive" },
    { label: "Avg latency", value: `${s?.sandbox_avg_ms ?? 0}ms`, sub: "p50 sandbox", icon: Zap, color: "primary" },
    { label: "Workflow ms", value: `${s?.workflow_avg_ms ?? 0}ms`, sub: "p50 wf", icon: Cpu, color: "secondary" },
    { label: "Cost 24h", value: `$${(((s?.sandbox_runs ?? 0) + (s?.chat_messages ?? 0)) * 0.003).toFixed(2)}`, sub: "estimado", icon: DollarSign, color: "success" },
  ];

  const max = Math.max(1, ...(s?.by_tool ?? []).map((t) => t.count));

  return (
    <div className="h-full overflow-auto p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-display font-bold flex items-center gap-2">
          <Cpu className="w-6 h-6 text-primary" /> DevOps Studio
        </h2>
        <p className="text-sm text-muted-foreground">Telemetria real (últimas 24h) · live via realtime</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="glass-panel p-4 rounded-lg border border-primary/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{c.label}</span>
                  <Icon className="w-4 h-4 text-primary" />
                </div>
                <div className="text-2xl font-bold font-display">{c.value}</div>
                <div className="text-xs text-muted-foreground">{c.sub}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="glass-panel p-5 rounded-lg border border-primary/20">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold">Top tools by usage (24h)</h3>
        </div>
        {loading ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-5" />)}</div>
        ) : (s?.by_tool.length ?? 0) === 0 ? (
          <p className="text-xs text-muted-foreground py-8 text-center">
            Sem runs — execute uma tool em Tools Galaxy para popular.
          </p>
        ) : (
          <div className="space-y-2">
            {s!.by_tool.map((t) => (
              <div key={t.name} className="flex items-center gap-3">
                <span className="text-xs font-mono w-40 truncate">{t.name}</span>
                <div className="flex-1 h-5 bg-surface-2 rounded overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary/80 to-accent/80 transition-all"
                    style={{ width: `${(t.count / max) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-mono w-12 text-right text-muted-foreground">{t.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
